import * as THREE from 'three'
import { World } from '../sim/world'
import { MapDef } from '../data/maps'
import { damp } from '../core/math'
import { buildAtlas, enemySpriteName, pickupSpriteName, projSpriteName, AtlasResult } from './pixelart'
import { SpriteBatch } from './spriteBatch'
import { CreatureBatch, PlayerMesh, Archetype } from './creatures'
import { PropBatch, buildPropBatches } from './propmeshes'
import { ENEMIES } from '../data/enemies'
import { terrainHeight, terrainSlope, mapSeed, resolveTerrain, TerrainConfig } from '../core/terrain'
import { dayLight, DAY_LENGTH, enemyDuskTint } from '../core/daylight'
import { weatherParams } from './weather'
import { boundaryTheme } from './boundary'
import { pickupGlint, pickupGlow } from './pickupfx'
import { burstSpec, burstAt, BurstSpec } from './burst'
import { hitPop, FLASH_MAX } from './hitfx'
import { propSway } from './propsway'
import { dustKick } from './dustfx'
import { projectileSpriteSize } from './projfx'
import { telegraphLook } from './telegraphfx'
import { lookAheadOffset, cameraOrbit } from './camerafx'

const PARTICLE_CAP = 3000
const AURA_POOL = 24
const WEATHER_CAP = 140 // pooled ambient motes (dust/sand/spores/embers)

// route an enemy to a 3D body archetype by its role, so the swarm reads as varied creatures (not identical blobs)
function enemyArchetype(defId: string, isBoss: boolean): Archetype {
  if (isBoss) return 'brute'
  const ai = ENEMIES[defId]?.ai
  if (ai === 'ranged' || ai === 'buffer' || ai === 'healer') return 'caster'
  if (ai === 'orbiter' || ai === 'flanker') return 'flyer'
  if (ai === 'armored') return 'brute'
  return 'blob'
}
// a stable per-position yaw so identical props don't all face the same way (deterministic, render-only)
function propRotY(x: number, z: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453
  return (s - Math.floor(s)) * Math.PI * 2
}

// time-of-run tints: a warm sunset the world passes through, then a cool dusk it settles into
const WARM_TINT = new THREE.Color(0xff9a4d)
const DUSK_TINT = new THREE.Color(0x2b2b58)

const SHRINE_SPRITE: Record<string, string> = {
  magnet: 'prop:shrine_magnet',
  gold: 'prop:shrine_gold',
  heal: 'prop:shrine_heal',
  treasure: 'prop:shrine_treasure',
  battle: 'prop:shrine_battle',
  curse: 'prop:shrine_curse',
}

const _v = new THREE.Vector3()
const _color = new THREE.Color()
const _lc = new THREE.Color() // scratch for per-frame time-of-run light tinting

export class Renderer {
  readonly scene = new THREE.Scene()
  readonly camera: THREE.PerspectiveCamera
  readonly renderer: THREE.WebGLRenderer

  private atlas: AtlasResult
  private batch: SpriteBatch
  private creatures!: Record<Archetype, CreatureBatch>
  private corpseBatch!: CreatureBatch
  private playerMesh!: PlayerMesh
  private propBatches!: Record<string, PropBatch>
  private hasProp!: (sprite: string) => boolean
  private particles: THREE.Points
  private particleGeom: THREE.BufferGeometry
  // per-biome ambient weather motes (pooled THREE.Points; render-only)
  private weather!: THREE.Points
  private weatherGeom!: THREE.BufferGeometry
  private weatherJitter = new Float32Array(WEATHER_CAP) // per-mote drift variation
  private weatherCount = 0
  private weatherDrift = 0
  private weatherRise = 0
  private weatherArea = 1
  private weatherHeight = 1
  private auraPool: THREE.Mesh[] = []
  private telegraphPool: THREE.Mesh[] = []
  private telegraphRimPool: THREE.Mesh[] = []
  private telegraphFusePool: THREE.Mesh[] = []
  private glowPool: THREE.Mesh[] = [] // highlight rings under elites/bosses
  private shadowPool: THREE.Mesh[] = [] // soft contact shadows so billboards rest on the terrain
  private pickupGlowPool: THREE.Mesh[] = [] // additive ground-glow beacons under valuable pickups
  private blastPool: THREE.Mesh[] = [] // CF2: expanding footprint rings for explosions
  private burstPool: { mesh: THREE.Mesh; t: number; spec: BurstSpec; active: boolean }[] = [] // level-up/evolve rings
  // dust kicked up under the running player (pooled render-only motes)
  private dustGeom!: THREE.BufferGeometry
  private dustVel = new Float32Array(64 * 3)
  private dustLife = new Float32Array(64)
  private dustAcc = 0
  private dustColor = new THREE.Color(0xbfb29a)
  private ground!: THREE.Mesh
  private boundary!: THREE.Mesh
  private boundaryMat?: THREE.MeshStandardMaterial
  private wall?: THREE.Mesh
  private boundaryEmissive = 0.5
  private wallOpacity = 0.06
  private boundaryPulse = 1.5
  private grid?: THREE.GridHelper
  private playerColor = new THREE.Color(0xffcc44)
  private playerSprite = 'hero' // AO5: per-character body archetype (heroSpriteName)
  private terrainSeed = 0
  private terrainAmp = 0
  private terrainCfg: TerrainConfig = resolveTerrain()
  private hemi!: THREE.HemisphereLight
  private sun!: THREE.DirectionalLight
  // per-biome base lighting captured in setMap; sync() lerps from these toward the time-of-run tint
  private baseSunColor = new THREE.Color(0xffffff)
  private baseHemiSky = new THREE.Color(0xffffff)
  private baseFog = new THREE.Color(0xffffff)
  private baseSunIntensity = 1.0
  private baseHemiIntensity = 1.1
  private fogNear = 0
  private fogFar = 0

  private trauma = 0 // 0..1 screen-shake energy (squared for punch, decays fast)
  private zoomPunch = 0 // transient camera zoom-in on big events
  private camTarget = new THREE.Vector3()
  private camLead = new THREE.Vector2() // CO3: eased look-ahead offset toward player movement
  camYaw = 0 // FEAT-C: free-orbit camera yaw (radians); 0 = behind the player on +z

  constructor(container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' })
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
    this.renderer.setSize(innerWidth, innerHeight)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    // cozy "chunky 3D" grade: soft filmic tone mapping + a gentle warm exposure so the pastel palette never blows out
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.12
    // soft blurred shadows give the diorama / toy-box read; cheap PCF-soft at a single sun
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    container.appendChild(this.renderer.domElement)
    this.attachYawDrag(this.renderer.domElement)

    this.camera = new THREE.PerspectiveCamera(52, innerWidth / innerHeight, 0.5, 400)
    this.camera.position.set(0, 10.5, 13) // Megabonk-style close low-angle follow (see updateCamera)
    this.camera.lookAt(0, 0, 0)

    // Three-light cozy rig (Pokopia / cozy-low-poly recipe): pale-blue sky fill + warm ground bounce (hemisphere),
    // a low ambient so shadows never go black, and one warm golden sun for gentle shaping + a soft shadow.
    this.hemi = new THREE.HemisphereLight(0x9fd8ff, 0xb9966b, 0.62)
    this.scene.add(this.hemi)
    this.scene.add(new THREE.AmbientLight(0xfff2dc, 0.34))
    this.sun = new THREE.DirectionalLight(0xffd9a0, 0.95)
    this.sun.position.set(14, 26, 10)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(2048, 2048)
    this.sun.shadow.radius = 4
    this.sun.shadow.bias = -0.0006
    this.sun.shadow.normalBias = 0.03
    const sc = this.sun.shadow.camera as THREE.OrthographicCamera
    sc.near = 1
    sc.far = 80
    sc.left = -26; sc.right = 26; sc.top = 26; sc.bottom = -26 // a focused frustum that follows the player (see sync)
    sc.updateProjectionMatrix()
    this.scene.add(this.sun)
    this.scene.add(this.sun.target)

    // pixel-art sprite atlas + batch (one draw call for the whole swarm) — still used for props/projectiles/pickups
    this.atlas = buildAtlas()
    this.batch = new SpriteBatch(this.atlas, 3600)
    this.scene.add(this.batch.mesh)
    // 3D creature meshes (enemies, by body archetype) + corpses + the hero mesh — rounded lit 3D, not billboards
    this.creatures = {
      blob: new CreatureBatch('blob', 2200),
      flyer: new CreatureBatch('flyer', 1400),
      brute: new CreatureBatch('brute', 700),
      caster: new CreatureBatch('caster', 700),
    }
    for (const k of Object.keys(this.creatures) as Archetype[]) this.scene.add(this.creatures[k].mesh)
    this.corpseBatch = new CreatureBatch('blob', 512)
    this.scene.add(this.corpseBatch.mesh)
    this.playerMesh = new PlayerMesh()
    this.scene.add(this.playerMesh.group)
    // 3D meshes for all map objects (props / obstacles / breakables) — one instanced batch per type
    const pb = buildPropBatches(256)
    this.propBatches = pb.batches
    this.hasProp = pb.has
    for (const k of Object.keys(this.propBatches)) this.scene.add(this.propBatches[k].mesh)

    // translucent aura rings (for aura-type weapons)
    const ringGeom = new THREE.CircleGeometry(1, 36)
    ringGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < AURA_POOL; i++) {
      const m = new THREE.Mesh(
        ringGeom,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.18,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.auraPool.push(m)
      this.scene.add(m)
    }

    // telegraph decals (boss attack warnings). Pool must cover the worst-case simultaneous count: the enrage NOVA
    // alone queues 17 discs in one frame and can overlap a still-live slam/ring — an under-sized pool left the
    // surplus discs UNDRAWN while updateTelegraphs still dealt their damage (invisible, undodgeable AoE). Sized for
    // TWO concurrent bosses (the spawner has no alive-boss guard): 2× nova (34) + slam/ring overlap headroom = 40.
    for (let i = 0; i < 40; i++) {
      const m = new THREE.Mesh(
        ringGeom,
        new THREE.MeshBasicMaterial({
          color: 0xff3a2a,
          transparent: true,
          opacity: 0.3,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.telegraphPool.push(m)
      this.scene.add(m)
    }
    // CO1: a bright rim ring + a contracting "fuse" ring make the danger zone + its timing readable
    const tgRimGeom = new THREE.RingGeometry(0.85, 1, 44)
    tgRimGeom.rotateX(-Math.PI / 2)
    const tgFuseGeom = new THREE.RingGeometry(0.74, 0.92, 36)
    tgFuseGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < 40; i++) { // matches the disc pool (40) so rim+fuse never lag behind the fill disc on a two-boss frame
      const rim = new THREE.Mesh(tgRimGeom, new THREE.MeshBasicMaterial({ color: 0xff5a2a, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }))
      rim.visible = false
      rim.frustumCulled = false
      this.telegraphRimPool.push(rim)
      this.scene.add(rim)
      const fuse = new THREE.Mesh(tgFuseGeom, new THREE.MeshBasicMaterial({ color: 0xffe27a, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }))
      fuse.visible = false
      fuse.frustumCulled = false
      this.telegraphFusePool.push(fuse)
      this.scene.add(fuse)
    }

    // highlight rings that pulse under elites (gold) and bosses (red) so threats
    // are readable in a dense swarm
    for (let i = 0; i < 20; i++) {
      const m = new THREE.Mesh(
        ringGeom,
        new THREE.MeshBasicMaterial({
          color: 0xffd23d,
          transparent: true,
          opacity: 0.3,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.glowPool.push(m)
      this.scene.add(m)
    }

    // soft contact shadows: flat dark discs pooled under the player + nearby enemies so
    // billboards sit ON the terrain (and slopes read) instead of floating above it
    const shadowGeom = new THREE.CircleGeometry(1, 18)
    shadowGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < 48; i++) {
      const m = new THREE.Mesh(
        shadowGeom,
        new THREE.MeshBasicMaterial({
          color: 0x000000,
          transparent: true,
          opacity: 0.28,
          depthWrite: false,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.shadowPool.push(m)
      this.scene.add(m)
    }

    // pickup ground-glow beacons: additive discs under valuable loot so the eye finds it in a dense field
    const glowDiscGeom = new THREE.CircleGeometry(1, 20)
    glowDiscGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < 20; i++) {
      const m = new THREE.Mesh(
        glowDiscGeom,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.25,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.pickupGlowPool.push(m)
      this.scene.add(m)
    }

    // CF2: explosion footprint rings — a thin unit ring (scaled to the blast radius) that flashes the
    // actual lethal AoE so the kill zone is readable (was an under-sized particle puff)
    const blastRingGeom = new THREE.RingGeometry(0.86, 1, 40)
    blastRingGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < 16; i++) {
      const m = new THREE.Mesh(
        blastRingGeom,
        new THREE.MeshBasicMaterial({
          color: 0xff7a2d,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.blastPool.push(m)
      this.scene.add(m)
    }

    // world feedback bursts: expanding ground rings pulsed on level-up / evolve
    const burstGeom = new THREE.RingGeometry(0.82, 1, 40)
    burstGeom.rotateX(-Math.PI / 2)
    for (let i = 0; i < 6; i++) {
      const m = new THREE.Mesh(
        burstGeom,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
        }),
      )
      m.visible = false
      m.frustumCulled = false
      this.burstPool.push({ mesh: m, t: 0, spec: burstSpec('levelup'), active: false })
      this.scene.add(m)
    }

    // dust motes kicked up under the running player (pooled, additive, fades via vertex colour)
    this.dustGeom = new THREE.BufferGeometry()
    this.dustGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(64 * 3), 3))
    this.dustGeom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(64 * 3), 3))
    this.dustGeom.setDrawRange(0, 0)
    const dustMat = new THREE.PointsMaterial({
      size: 0.4,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    const dust = new THREE.Points(this.dustGeom, dustMat)
    dust.frustumCulled = false
    this.scene.add(dust)

    // particles
    this.particleGeom = new THREE.BufferGeometry()
    this.particleGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(PARTICLE_CAP * 3), 3))
    this.particleGeom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(PARTICLE_CAP * 3), 3))
    const partMat = new THREE.PointsMaterial({
      size: 0.32,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    this.particles = new THREE.Points(this.particleGeom, partMat)
    this.particles.frustumCulled = false
    this.scene.add(this.particles)

    // ambient weather motes (pooled; tinted + configured per biome in setMap)
    this.weatherGeom = new THREE.BufferGeometry()
    this.weatherGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(WEATHER_CAP * 3), 3))
    const weatherMat = new THREE.PointsMaterial({
      size: 0.18,
      color: 0xffffff,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    })
    this.weather = new THREE.Points(this.weatherGeom, weatherMat)
    this.weather.frustumCulled = false
    this.scene.add(this.weather)

    window.addEventListener('resize', this.onResize)
  }

  private onResize = () => {
    this.camera.aspect = innerWidth / innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(innerWidth, innerHeight)
  }

  setMap(map: MapDef): void {
    // dispose the prior arena's GPU resources before replacing them — scene.remove alone leaks the geometry +
    // material buffers, and setMap runs once per run, so VRAM would climb every Play→Quit→Play (mobile target)
    if (this.ground) { this.scene.remove(this.ground); this.ground.geometry.dispose(); (this.ground.material as THREE.Material).dispose() }
    if (this.boundary) { this.scene.remove(this.boundary); this.boundary.geometry.dispose(); (this.boundary.material as THREE.Material).dispose() }
    if (this.grid) this.scene.remove(this.grid)

    this.scene.background = new THREE.Color(map.fogColor)
    // per-biome fog depth (scales with arena size) so each map has its own sense of distance
    this.fogNear = map.radius * 0.5
    this.fogFar = map.radius * 1.45
    this.scene.fog = new THREE.Fog(map.fogColor, this.fogNear, this.fogFar)
    // tint the lighting toward the biome accent so each world feels atmospherically distinct
    const sky = new THREE.Color(0xffffff).lerp(new THREE.Color(map.accentColor), 0.18)
    this.hemi.color.copy(sky)
    this.hemi.groundColor = new THREE.Color(map.fogColor).lerp(new THREE.Color(0x404060), 0.5)
    this.sun.color = new THREE.Color(0xffffff).lerp(new THREE.Color(map.accentColor), 0.1)
    // capture the per-biome base so the time-of-run arc (sync) lerps from it, not compounding
    this.baseSunColor.copy(this.sun.color)
    this.baseHemiSky.copy(this.hemi.color)
    this.baseFog.copy(this.scene.fog.color)
    this.baseSunIntensity = this.sun.intensity
    this.baseHemiIntensity = this.hemi.intensity

    // procedural terrain: a displaced grid so the planet has real elevation
    this.terrainSeed = mapSeed(map.id)
    this.terrainAmp = map.elevation ?? 0
    this.terrainCfg = resolveTerrain(map.terrain)
    const size = (map.radius + 6) * 2
    const seg = 112
    const groundGeom = new THREE.PlaneGeometry(size, size, seg, seg)
    groundGeom.rotateX(-Math.PI / 2)
    const pos = groundGeom.getAttribute('position') as THREE.BufferAttribute
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, terrainHeight(pos.getX(i), pos.getZ(i), this.terrainSeed, this.terrainAmp, this.terrainCfg) - 0.02)
    }
    pos.needsUpdate = true
    groundGeom.computeVertexNormals()
    // shade the ground by elevation + slope (relief reads), then tint peaks toward the biome
    // accent and deepen basins toward the fog colour, so each map reads as a distinct, lush place
    const norm = groundGeom.getAttribute('normal') as THREE.BufferAttribute
    const colors = new Float32Array(pos.count * 3)
    const ampN = Math.max(0.5, this.terrainAmp)
    const base = new THREE.Color(map.groundColor)
    const accent = new THREE.Color(map.accentColor)
    const deep = new THREE.Color(map.fogColor)
    for (let i = 0; i < pos.count; i++) {
      const h = pos.getY(i) + 0.02
      const hn = Math.max(-1, Math.min(1, h / ampN)) // -1 (basin) .. 1 (peak)
      const ny = Math.max(0, norm.getY(i)) // 1 flat .. 0 vertical
      let shade = (0.68 + hn * 0.6) * (0.76 + 0.24 * ny)
      shade = Math.max(0.24, Math.min(1.4, shade))
      let r = base.r * shade
      let g = base.g * shade
      let b = base.b * shade
      // high, flat ground catches a subtle biome-accent highlight
      const peakT = Math.max(0, hn) * 0.16 * ny
      r = r * (1 - peakT) + accent.r * peakT
      g = g * (1 - peakT) + accent.g * peakT
      b = b * (1 - peakT) + accent.b * peakT
      // deep basins sink toward the fog/shadow colour for moody lows
      const basinT = Math.max(0, -hn) * 0.3
      r = r * (1 - basinT) + deep.r * basinT
      g = g * (1 - basinT) + deep.g * basinT
      b = b * (1 - basinT) + deep.b * basinT
      colors[i * 3] = r
      colors[i * 3 + 1] = g
      colors[i * 3 + 2] = b
    }
    groundGeom.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    // vertex colours now carry the full tint; keep material white so it isn't double-multiplied
    const groundMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.98, flatShading: true, vertexColors: true })
    this.ground = new THREE.Mesh(groundGeom, groundMat)
    this.ground.receiveShadow = true // catch the soft creature/hero shadows for the cozy diorama read
    this.scene.add(this.ground)

    // per-biome arena edge: a glowing ring + a translucent containment wall that reads the world's bounds
    const bt = boundaryTheme(map.id)
    this.boundaryEmissive = bt.emissive
    this.wallOpacity = bt.wallOpacity
    this.boundaryPulse = bt.pulse
    const ringGeom = new THREE.TorusGeometry(map.radius, bt.tube, 8, 96)
    ringGeom.rotateX(Math.PI / 2)
    this.boundaryMat = new THREE.MeshStandardMaterial({ color: map.accentColor, emissive: map.accentColor, emissiveIntensity: bt.emissive })
    this.boundary = new THREE.Mesh(ringGeom, this.boundaryMat)
    this.scene.add(this.boundary)

    if (this.wall) { this.scene.remove(this.wall); this.wall.geometry.dispose(); (this.wall.material as THREE.Material).dispose() }
    const wallGeom = new THREE.CylinderGeometry(map.radius, map.radius, bt.wallHeight, 96, 1, true)
    const wallMat = new THREE.MeshBasicMaterial({
      color: map.accentColor,
      transparent: true,
      opacity: bt.wallOpacity,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    this.wall = new THREE.Mesh(wallGeom, wallMat)
    this.wall.position.y = bt.wallHeight / 2
    this.scene.add(this.wall)

    // dust tints toward the biome ground, brightened (kicked-up soil catches the light)
    this.dustColor.set(map.groundColor).multiplyScalar(1.6)

    this.configureWeather(map.id)
  }

  /** Tint + scatter the ambient weather motes for a biome (called from setMap). */
  private configureWeather(mapId: string): void {
    const w = weatherParams(mapId)
    this.weatherCount = Math.min(WEATHER_CAP, w.count)
    this.weatherDrift = w.drift
    this.weatherRise = w.rise
    this.weatherArea = w.area
    this.weatherHeight = w.height
    const mat = this.weather.material as THREE.PointsMaterial
    mat.color.setHex(w.color)
    mat.size = w.size
    mat.opacity = w.opacity
    // additive embers glow; settling dust/sand stays normal so it reads as haze
    mat.blending = w.rise > 0.8 ? THREE.AdditiveBlending : THREE.NormalBlending
    mat.needsUpdate = true
    const pos = this.weatherGeom.getAttribute('position') as THREE.BufferAttribute
    const cx = this.camera.position.x
    const cz = this.camera.position.z
    for (let i = 0; i < WEATHER_CAP; i++) {
      pos.setXYZ(
        i,
        cx + (Math.random() * 2 - 1) * w.area,
        Math.random() * w.height,
        cz + (Math.random() * 2 - 1) * w.area,
      )
      this.weatherJitter[i] = 0.4 + Math.random() * 1.2
    }
    pos.needsUpdate = true
    this.weatherGeom.setDrawRange(0, this.weatherCount)
  }

  /** Drift the weather motes (wind + rise/settle), wrapping them around the player. */
  private updateWeather(dt: number): void {
    if (this.weatherCount === 0) return
    const pos = this.weatherGeom.getAttribute('position') as THREE.BufferAttribute
    const cx = this.camera.position.x
    const cz = this.camera.position.z
    const a = this.weatherArea
    const h = this.weatherHeight
    for (let i = 0; i < this.weatherCount; i++) {
      const j = this.weatherJitter[i]
      let x = pos.getX(i) + this.weatherDrift * j * dt
      let y = pos.getY(i) + this.weatherRise * j * dt
      let z = pos.getZ(i) + this.weatherDrift * 0.3 * j * dt
      // vertical wrap (recycle through the band)
      if (y > h) y = 0
      else if (y < 0) y = h
      // keep the field centred on the player by wrapping horizontally
      if (x - cx > a) x -= 2 * a
      else if (x - cx < -a) x += 2 * a
      if (z - cz > a) z -= 2 * a
      else if (z - cz < -a) z += 2 * a
      pos.setXYZ(i, x, y, z)
    }
    pos.needsUpdate = true
  }

  setPlayerColor(color: number): void {
    this.playerColor.setHex(color)
  }

  setPlayerSprite(name: string): void {
    this.playerSprite = name
  }

  /** Ground elevation at a world position (entities rest on the terrain). */
  private groundY(x: number, z: number): number {
    return terrainHeight(x, z, this.terrainSeed, this.terrainAmp, this.terrainCfg)
  }

  addShake(amount: number, max = 0.85): void {
    // feed trauma (squared in sync for a punchier, faster-decaying shake). `max` caps the CONTINUOUS combat channel
    // (kills/crits/recoil) so a dense stream can't saturate trauma into a constant screen buzz; a bigger event already
    // above `max` (hurt / boss / evolve) is left untouched — a small rattle must never cut an ongoing slam.
    if (this.trauma >= max) return
    this.trauma = Math.min(max, this.trauma + amount * 0.32)
  }

  /** Transient zoom-in jolt for big moments (boss/evolve/nuke/revive). */
  punch(amount: number): void {
    this.zoomPunch = Math.min(4, this.zoomPunch + amount)
  }

  /** FEAT-C: rotate the free-orbit camera yaw (radians, wrapped). */
  rotateYaw(delta: number): void {
    this.camYaw = (this.camYaw + delta) % (Math.PI * 2)
  }

  /** FEAT-C: drag the 3D canvas (mouse or touch) to orbit the camera yaw. */
  private attachYawDrag(el: HTMLElement): void {
    let dragging = false
    let lastX = 0
    el.style.touchAction = 'none' // FEAT-B: don't let the browser scroll/zoom on canvas touch
    el.addEventListener('pointerdown', (e) => {
      dragging = true
      lastX = e.clientX
      try { el.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    })
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return
      const dx = e.clientX - lastX
      lastX = e.clientX
      this.rotateYaw(-dx * 0.006) // drag right → orbit right
    })
    const end = () => { dragging = false }
    el.addEventListener('pointerup', end)
    el.addEventListener('pointercancel', end)
    el.addEventListener('lostpointercapture', end)
  }

  worldToScreen(x: number, y: number, z: number): { x: number; y: number; visible: boolean } {
    _v.set(x, y, z).project(this.camera)
    return {
      x: (_v.x * 0.5 + 0.5) * innerWidth,
      y: (-_v.y * 0.5 + 0.5) * innerHeight,
      visible: _v.z < 1,
    }
  }

  /**
   * Drift the scene lighting/fog along the time-of-run arc (day → sunset → dusk) so the world
   * visibly escalates as a run goes deep. Lerps from the per-biome base captured in setMap, so it
   * never compounds frame to frame and stays distinct per map. Pure presentation — no sim impact.
   */
  private applyDayLight(time: number): void {
    if (!this.scene.fog) return
    const d = dayLight(time, DAY_LENGTH)
    // sun: dim with intensity, pass through warm sunset, settle into cool dusk
    this.sun.intensity = this.baseSunIntensity * d.intensity
    _lc.copy(this.baseSunColor).lerp(WARM_TINT, d.warm * 0.4).lerp(DUSK_TINT, d.cool * 0.5)
    this.sun.color.copy(_lc)
    // hemisphere ambient: keep more of its level (readability) but cool the sky toward dusk
    this.hemi.intensity = this.baseHemiIntensity * (0.8 + 0.2 * d.intensity)
    _lc.copy(this.baseHemiSky).lerp(DUSK_TINT, d.cool * 0.4)
    this.hemi.color.copy(_lc)
    // fog: darken toward dusk and pull the horizon in as the run gets dangerous
    _lc.copy(this.baseFog).lerp(DUSK_TINT, d.cool * 0.45)
    ;(this.scene.fog as THREE.Fog).color.copy(_lc)
    ;(this.scene.background as THREE.Color).copy(_lc)
    ;(this.scene.fog as THREE.Fog).near = this.fogNear * d.fogScale
    ;(this.scene.fog as THREE.Fog).far = this.fogFar * d.fogScale
  }

  /** Pulse an expanding world-feedback ring at a position (level-up / evolve). */
  triggerBurst(x: number, z: number, kind: string): void {
    const slot = this.burstPool.find((b) => !b.active) ?? this.burstPool[0]
    slot.active = true
    slot.t = 0
    slot.spec = burstSpec(kind)
    slot.mesh.position.set(x, this.groundY(x, z) + 0.12, z)
    ;(slot.mesh.material as THREE.MeshBasicMaterial).color.setHex(slot.spec.color)
  }

  /** Advance active feedback rings (expand + fade); deactivate when spent. */
  private updateBursts(dt: number): void {
    for (const b of this.burstPool) {
      if (!b.active) continue
      b.t += dt
      const { radius, alpha } = burstAt(b.spec, b.t)
      if (b.t >= b.spec.duration) {
        b.active = false
        b.mesh.visible = false
        continue
      }
      b.mesh.visible = true
      b.mesh.scale.setScalar(Math.max(0.01, radius))
      ;(b.mesh.material as THREE.MeshBasicMaterial).opacity = alpha
    }
  }

  /** Kick up dust motes under the player as they run (more + faster on dashes). Render-only. */
  private updateDust(dt: number, px: number, pz: number, py: number, speed: number): void {
    const N = this.dustLife.length
    const pos = this.dustGeom.getAttribute('position') as THREE.BufferAttribute
    const col = this.dustGeom.getAttribute('color') as THREE.BufferAttribute
    const intensity = dustKick(speed)
    // spawn budget scales with how hard the player is moving
    this.dustAcc += intensity * dt * 38
    let spawn = Math.floor(this.dustAcc)
    this.dustAcc -= spawn
    const MAX_LIFE = 0.5
    for (let i = 0; i < N; i++) {
      if (this.dustLife[i] <= 0) {
        if (spawn > 0) {
          spawn--
          this.dustLife[i] = MAX_LIFE * (0.6 + Math.random() * 0.4)
          pos.setXYZ(i, px + (Math.random() - 0.5) * 0.8, py + 0.05, pz + (Math.random() - 0.5) * 0.8)
          this.dustVel[i * 3] = (Math.random() - 0.5) * 1.2
          this.dustVel[i * 3 + 1] = 0.6 + Math.random() * 0.5
          this.dustVel[i * 3 + 2] = (Math.random() - 0.5) * 1.2
        } else {
          col.setXYZ(i, 0, 0, 0)
          continue
        }
      }
      this.dustLife[i] -= dt
      const a = Math.max(0, this.dustLife[i] / MAX_LIFE)
      this.dustVel[i * 3 + 1] -= 1.4 * dt // settle back down
      pos.setXYZ(i, pos.getX(i) + this.dustVel[i * 3] * dt, pos.getY(i) + this.dustVel[i * 3 + 1] * dt, pos.getZ(i) + this.dustVel[i * 3 + 2] * dt)
      col.setXYZ(i, this.dustColor.r * a, this.dustColor.g * a, this.dustColor.b * a)
    }
    this.dustGeom.setDrawRange(0, N)
    pos.needsUpdate = true
    col.needsUpdate = true
  }

  sync(world: World, dt: number, shakeEnabled: boolean): void {
    const p = world.player
    this.applyDayLight(world.time)
    this.updateWeather(dt)
    this.updateBursts(dt)
    if (p.alive) this.updateDust(dt, p.x, p.z, this.groundY(p.x, p.z) + p.y, Math.hypot(p.vx, p.vz))
    else this.updateDust(dt, p.x, p.z, this.groundY(p.x, p.z), 0)
    // gently pulse the arena edge (ring glow + wall haze) so the boundary feels alive per biome
    if (this.boundaryMat) {
      const pl = 0.5 + 0.5 * Math.sin(world.time * this.boundaryPulse)
      this.boundaryMat.emissiveIntensity = this.boundaryEmissive * (0.75 + 0.35 * pl)
      if (this.wall) (this.wall.material as THREE.MeshBasicMaterial).opacity = this.wallOpacity * (0.7 + 0.6 * pl)
    }
    this.camera.updateMatrixWorld() // ensure billboards use the camera's current matrix (esp. the first frame)
    this.batch.begin(this.camera)
    for (const k of Object.keys(this.creatures) as Archetype[]) this.creatures[k].begin()
    this.corpseBatch.begin()
    for (const k of Object.keys(this.propBatches)) this.propBatches[k].begin()

    // static environment props (scenery) — now chunky 3D primitives; tall ones still sway gently in the wind
    for (const pr of world.props) {
      const sway = propSway(pr.sprite, world.time, pr.x * 0.7 + pr.z * 0.3)
      const gy = this.groundY(pr.x, pr.z)
      if (this.hasProp(pr.sprite)) this.propBatches[pr.sprite].add(pr.x + sway.dx, gy + sway.dy, pr.z, pr.scale, propRotY(pr.x, pr.z), 1)
      else this.batch.add(pr.sprite, pr.x + sway.dx, gy + sway.dy, pr.z, pr.scale, 1, 1, 1) // unknown sprite → billboard fallback
    }
    // solid obstacles (block movement) — chunky 3D
    for (const o of world.obstacles) {
      const gy = this.groundY(o.x, o.z)
      if (this.hasProp(o.sprite)) this.propBatches[o.sprite].add(o.x, gy, o.z, o.scale, propRotY(o.x, o.z), 1)
      else this.batch.add(o.sprite, o.x, gy, o.z, o.scale, 1, 1, 1)
    }
    // destructible loot objects (brighten when hit) — chunky 3D
    for (const b of world.breakables) {
      const f = b.flash > 0 ? 2.2 : 1
      const gy = this.groundY(b.x, b.z)
      if (this.hasProp(b.sprite)) this.propBatches[b.sprite].add(b.x, gy, b.z, b.scale, propRotY(b.x, b.z), f)
      else this.batch.add(b.sprite, b.x, gy, b.z, b.scale, f, f, f)
    }

    // player hero — a rounded 3D character (body + big soft head + eyes), tinted by character colour; flashes on hit
    if (p.alive) {
      const moving = Math.hypot(p.vx, p.vz) > 0.5
      const pbob = this.groundY(p.x, p.z) + p.y + (moving ? Math.abs(Math.sin(world.time * 12)) * 0.12 : Math.sin(world.time * 3) * 0.03)
      this.playerMesh.update(p.x, pbob, p.z, p.facing, this.playerColor.getHex(), p.flash > 0 ? 2.4 : 1.0)
      this.playerMesh.group.visible = true
    } else {
      this.playerMesh.group.visible = false
    }

    // enemies (+ collect elites/bosses for highlight rings)
    // ordinary swarm picks up a subtle dusk "menace" tint late-run (identity at day → no early change)
    const dusk = enemyDuskTint(dayLight(world.time).cool)
    let glowIdx = 0
    for (const e of world.enemies) {
      if (!e.alive) continue
      // hit micro-feedback: a size + brightness pop on impact, easing out as the flash fades
      const pop = hitPop(e.flash / FLASH_MAX)
      // brief size pop while staggered (knockback reads as an impact) + the fresh-hit pop
      const size = e.radius * 2.2 * e.scale * (e.stagger > 0 ? 1.14 : 1) * pop.scale
      let r = dusk.r
      let g = dusk.g
      let b = dusk.b
      if (e.flash > 0) {
        // brightness eases out with the flash instead of a hard binary white
        r = g = b = pop.bright
      } else if (e.bossShield > 0) {
        // shielded boss glows cyan (damage-immune window)
        r = 0.5
        g = 1.5
        b = 2.1
      } else if (e.freeze > 0) {
        r = 0.55
        g = 0.85
        b = 1.7
      } else if (e.empoweredT > 0) {
        // empowered (buffed by a War-Chanter / Overseer): pulsing hot-red glow. Tested BEFORE the mini-boss/warded/
        // elite identity tints — the buff (real +35% speed / +30% contact dmg) is the priority threat-telegraph and
        // has no other per-target cue; the identity tint returns the instant the buff expires.
        const pul = 1.2 + Math.abs(Math.sin(world.time * 9 + e.id)) * 0.5
        r = 1.7 * pul
        g = 0.7 * pul
        b = 0.5 * pul
      } else if (e.isMiniBoss) {
        // amber tint so the champion reads apart from gold elites
        r = 1.9
        g = 1.0
        b = 0.35
      } else if (e.affix === 'warded') {
        // steely blue so a frontal-armored elite reads as "flank me / crit me" (distinct from the gold elite)
        r = 0.7
        g = 0.9
        b = 1.7
      } else if (e.affix === 'swift') {
        // hot white-yellow so a fast elite reads as "it'll close on you" — kite it
        r = 1.9
        g = 1.7
        b = 0.7
      } else if (e.affix === 'mending') {
        // sickly green so a self-healing elite reads as "burst it down before it claws back"
        r = 0.5
        g = 1.7
        b = 0.7
      } else if (e.isElite) {
        // gold tint so elites pop out of the swarm (was dead code before)
        r = 1.6
        g = 1.35
        b = 0.4
      }
      // 3D rounded creature body, lit + shadow-casting, tinted by its own colour × the state tint (flash/freeze/elite…)
      const arch = enemyArchetype(e.defId, e.isBoss)
      const lift = arch === 'flyer' ? size * 0.32 : 0 // little flyers hover off the ground
      const ebob = (e.freeze > 0 ? 0 : Math.abs(Math.sin(world.time * 8 + e.id)) * 0.09) + this.groundY(e.x, e.z) + lift
      this.creatures[arch].add(e.x, ebob, e.z, size, e.facing, ENEMIES[e.defId]?.color ?? 0x9aa0aa, r, g, b)

      // pulsing highlight ring under elites (gold) and bosses (red)
      if ((e.isElite || e.isBoss) && glowIdx < this.glowPool.length) {
        const ring = this.glowPool[glowIdx++]
        ring.visible = true
        ring.position.set(e.x, this.groundY(e.x, e.z) + 0.05, e.z)
        ring.scale.setScalar(e.radius * e.scale * (e.isBoss ? 2.6 : e.isMiniBoss ? 2.1 : 1.7))
        const mat = ring.material as THREE.MeshBasicMaterial
        mat.color.setHex(e.isBoss ? 0xff3a2a : e.isMiniBoss ? 0xff7a2d : 0xffd23d)
        mat.opacity = 0.22 + Math.abs(Math.sin(world.time * 6 + e.id)) * 0.18
      }
    }
    for (let i = glowIdx; i < this.glowPool.length; i++) this.glowPool[i].visible = false

    // death pops: the 3D body rises, shrinks and briefly over-brightens as it fades
    for (const c of world.corpses) {
      const tt = c.life / c.maxLife
      const animScale = c.size * (0.2 + tt)
      const b = 1.0 + tt * 1.4
      this.corpseBatch.add(c.x, c.y + this.groundY(c.x, c.z), c.z, animScale, 0, c.color, b, b, b)
    }

    // CF2: explosion footprint rings — flash the actual lethal radius, expanding + fading
    let blIdx = 0
    for (const b of world.blasts) {
      if (blIdx >= this.blastPool.length) break
      const ring = this.blastPool[blIdx++]
      const prog = Math.max(0, Math.min(1, b.t / b.maxT))
      ring.visible = true
      ring.position.set(b.x, this.groundY(b.x, b.z) + 0.06, b.z)
      ring.scale.setScalar(b.radius * (0.72 + 0.28 * prog)) // expand toward the true radius
      const mat = ring.material as THREE.MeshBasicMaterial
      mat.color.setHex(b.color)
      mat.opacity = (1 - prog) * 0.6
    }
    for (let i = blIdx; i < this.blastPool.length; i++) this.blastPool[i].visible = false

    // projectiles: auras → ring pool; everything else → sprite
    let auraIdx = 0
    for (const pr of world.projectiles) {
      if (!pr.alive) continue
      if (pr.behavior === 'aura' && auraIdx < this.auraPool.length) {
        const ring = this.auraPool[auraIdx++]
        ring.visible = true
        ring.position.set(pr.x, this.groundY(pr.x, pr.z) + 0.06, pr.z)
        ring.scale.setScalar(pr.radius)
        ;(ring.material as THREE.MeshBasicMaterial).color.setHex(pr.color)
        continue
      }
      _color.setHex(pr.color)
      // CF3: sprite tracks the collision radius (reads where it hits), sat at a small constant height
      const psize = projectileSpriteSize(pr.radius)
      this.batch.add(projSpriteName(pr.ownerWeapon), pr.x, this.groundY(pr.x, pr.z) + pr.y + 0.45, pr.z, psize, _color.r * 1.4 + 0.3, _color.g * 1.4 + 0.3, _color.b * 1.4 + 0.3)
    }
    for (let i = auraIdx; i < this.auraPool.length; i++) this.auraPool[i].visible = false

    // telegraphs (boss AoE warnings — fill in, sharpen a bright rim, and contract a "fuse" ring as they
    // approach detonation so the kill zone + its timing read at a glance) — CO1
    let tgIdx = 0
    for (const tg of world.telegraphs) {
      if (tgIdx >= this.telegraphPool.length) break
      const look = telegraphLook(tg.timer, tg.maxTimer)
      const y = this.groundY(tg.x, tg.z)
      const pulse = 0.85 + 0.15 * Math.sin(world.time * 18)
      const disc = this.telegraphPool[tgIdx]
      disc.visible = true
      disc.position.set(tg.x, y + 0.07, tg.z)
      disc.scale.setScalar(tg.radius)
      ;(disc.material as THREE.MeshBasicMaterial).opacity = look.fillOpacity
      const rim = this.telegraphRimPool[tgIdx]
      rim.visible = true
      rim.position.set(tg.x, y + 0.08, tg.z)
      rim.scale.setScalar(tg.radius)
      ;(rim.material as THREE.MeshBasicMaterial).opacity = look.rimOpacity * pulse
      const fuse = this.telegraphFusePool[tgIdx]
      fuse.visible = true
      fuse.position.set(tg.x, y + 0.09, tg.z)
      fuse.scale.setScalar(Math.max(0.001, tg.radius * look.fuseScale))
      ;(fuse.material as THREE.MeshBasicMaterial).opacity = 0.85
      tgIdx++
    }
    for (let i = tgIdx; i < this.telegraphPool.length; i++) this.telegraphPool[i].visible = false
    for (let i = tgIdx; i < this.telegraphRimPool.length; i++) this.telegraphRimPool[i].visible = false
    for (let i = tgIdx; i < this.telegraphFusePool.length; i++) this.telegraphFusePool[i].visible = false

    // pickups (twinkling glint + a ground-glow beacon under the valuable ones)
    let pgIdx = 0
    for (const pk of world.pickups) {
      if (!pk.alive) continue
      let sprite = pickupSpriteName(pk.kind)
      let size =
        pk.kind === 'vendor' ? 1.8 : pk.kind === 'chest' ? 1.5 : pk.kind === 'heal' ? 1.0 : pk.kind === 'gold' ? 0.8 : 1.0
      const tier = pk.tier ?? 1
      if (pk.kind === 'xp') {
        // tiered gems: distinct sprite + size so value reads from the swarm
        sprite = `pickup:xp${tier}`
        size = tier === 3 ? 1.3 : tier === 2 ? 1.0 : 0.72
      }
      const gl = pickupGlint(world.time, pk.id)
      this.batch.add(sprite, pk.x, this.groundY(pk.x, pk.z) + pk.y + Math.sin(pk.bob) * 0.12, pk.z, size, gl, gl, gl)
      // ground-glow beacon for valuables (chest/vendor/heal/gold/big gems)
      const glow = pickupGlow(pk.kind, tier)
      if (glow > 0 && pgIdx < this.pickupGlowPool.length) {
        const disc = this.pickupGlowPool[pgIdx++]
        disc.visible = true
        disc.position.set(pk.x, this.groundY(pk.x, pk.z) + 0.05, pk.z)
        disc.scale.setScalar((1.1 + glow * 1.6) * (0.9 + 0.15 * Math.sin(world.time * 3 + pk.id)))
        const dm = disc.material as THREE.MeshBasicMaterial
        dm.color.setHex(pk.kind === 'vendor' || pk.kind === 'heal' ? 0x4dff9b : pk.kind === 'chest' || pk.kind === 'gold' ? 0xffcc33 : 0x49c7ff)
        dm.opacity = 0.12 + glow * 0.22
      }
    }
    for (let i = pgIdx; i < this.pickupGlowPool.length; i++) this.pickupGlowPool[i].visible = false

    // shrines / interactables — now 3D landmarks (microwave / moai / pedestal-shrines). Pulse + grow to read from range.
    for (const s of world.shrines) {
      const landmark = s.type === 'moai' || s.type === 'microwave'
      const sprite = s.type === 'moai' ? 'prop:moai' : s.type === 'microwave' ? 'prop:microwave' : SHRINE_SPRITE[s.type] ?? 'prop:shrine'
      const dim = s.used ? 0.4 : 1
      const pulse = s.used ? 1 : 1.05 + Math.sin(world.time * 4 + s.id) * 0.25
      const b = dim * pulse
      const bob = (s.used ? 0 : Math.sin(world.time * 2 + s.id) * 0.1) + this.groundY(s.x, s.z)
      const chg = s.type === 'charge' && !s.used ? (s.charge ?? 0) : 0
      const h = (landmark ? 2.7 : 2.0) + chg * 0.5
      if (this.hasProp(sprite)) this.propBatches[sprite].add(s.x, bob, s.z, h, propRotY(s.x, s.z), b)
      else this.batch.add(sprite, s.x, bob, s.z, h, b, b, b)
    }

    // boss teleporter — a spinning 3D portal ring that hovers + brightens as it channels toward activation
    const pt = world.portal
    if (pt && !pt.active) {
      const glow = 1 + Math.sin(world.time * 5) * 0.3 + pt.channel * 1.8
      const py = this.groundY(pt.x, pt.z) + 0.3 + Math.sin(world.time * 2) * 0.15
      const h = 2.8 + pt.channel * 0.9
      if (this.hasProp('prop:portal')) this.propBatches['prop:portal'].add(pt.x, py, pt.z, h, world.time * 0.8, glow)
      else this.batch.add('prop:portal', pt.x, py, pt.z, h, glow, glow, glow)
    }

    this.batch.end()
    for (const k of Object.keys(this.creatures) as Archetype[]) this.creatures[k].end()
    this.corpseBatch.end()
    for (const k of Object.keys(this.propBatches)) this.propBatches[k].end()

    // particles
    const posAttr = this.particleGeom.getAttribute('position') as THREE.BufferAttribute
    const colAttr = this.particleGeom.getAttribute('color') as THREE.BufferAttribute
    let qi = 0
    for (const pt of world.particles) {
      if (qi >= PARTICLE_CAP) break
      posAttr.setXYZ(qi, pt.x, this.groundY(pt.x, pt.z) + pt.y + 0.3, pt.z)
      _color.setHex(pt.color)
      const a = Math.max(0, pt.life / pt.maxLife)
      colAttr.setXYZ(qi, _color.r * a, _color.g * a, _color.b * a)
      qi++
    }
    this.particleGeom.setDrawRange(0, qi)
    posAttr.needsUpdate = true
    colAttr.needsUpdate = true

    // contact shadows under the player + nearby enemies (pooled, near-only for perf) so
    // sprites visibly rest on the ground and the terrain's slopes read
    let shIdx = 0
    if (p.alive && shIdx < this.shadowPool.length) {
      const sh = this.shadowPool[shIdx++]
      sh.visible = true
      sh.position.set(p.x, this.groundY(p.x, p.z) + 0.04, p.z)
      sh.scale.setScalar(0.95)
    }
    for (const e of world.enemies) {
      if (shIdx >= this.shadowPool.length) break
      if (!e.alive) continue
      const dx = e.x - p.x
      const dz = e.z - p.z
      if (dx * dx + dz * dz > 26 * 26) continue // only the on-screen cluster gets a shadow
      const sh = this.shadowPool[shIdx++]
      sh.visible = true
      sh.position.set(e.x, this.groundY(e.x, e.z) + 0.04, e.z)
      sh.scale.setScalar(Math.max(0.5, e.radius * e.scale * 1.1))
    }
    for (let i = shIdx; i < this.shadowPool.length; i++) this.shadowPool[i].visible = false

    // camera follow + shake (track the player's ground elevation)
    const pgy = this.groundY(p.x, p.z)
    // keep the sun's (focused) shadow frustum centred on the player so soft shadows render around the action
    this.sun.position.set(p.x + 14, pgy + 26, p.z + 10)
    this.sun.target.position.set(p.x, pgy, p.z)
    this.sun.target.updateMatrixWorld()
    // local steepness lifts the camera + look target a touch so ridges/basins reveal depth
    const slope = this.terrainAmp > 0 ? terrainSlope(p.x, p.z, this.terrainSeed, this.terrainAmp, this.terrainCfg) : 0
    // CO3: camera leads toward where the player is running, eased so direction flips don't snap
    const lead = lookAheadOffset(p.vx, p.vz)
    this.camLead.x = damp(this.camLead.x, lead.x, 3, dt)
    this.camLead.y = damp(this.camLead.y, lead.z, 3, dt)
    const fx = p.x + this.camLead.x
    const fz = p.z + this.camLead.y
    this.camTarget.set(fx, pgy + 0.6 + slope * 1.5, fz)
    // dynamic framing: ease back as the swarm grows; zoom-punch jolts in on big events
    const threat = Math.min(6, world.enemies.length * 0.012)
    // Megabonk-style framing: a CLOSE, low-angle 3rd-person follow locked to the player so the world reads
    // at real 1:1 scale (player is a clear focal point, terrain has depth) instead of a far top-down view.
    // base ≈ 13 horizontal : 10.5 up ⇒ ~39° pitch, ~16.7u out. Swarm-size eases back only a little.
    // zoom-punch is camera MOTION → honor the Screen Shake (reduce-motion) setting, like the trauma shake below
    const zp = shakeEnabled ? this.zoomPunch : 0
    const dist = 13 + threat * 0.6 - zp
    // FEAT-C: orbit the camera around the player by camYaw (yaw 0 = the old behind-on-+z framing)
    const orbit = cameraOrbit(this.camYaw, dist)
    this.camera.position.x = damp(this.camera.position.x, fx + orbit.x, 8, dt)
    this.camera.position.z = damp(this.camera.position.z, fz + orbit.z, 8, dt)
    this.camera.position.y = damp(this.camera.position.y, pgy + 10.5 + threat * 0.45 - zp * 0.4 + slope * 4, 8, dt)
    this.zoomPunch = Math.max(0, this.zoomPunch - dt * 6)
    // trauma-based shake: offset ∝ trauma² so light hits barely rattle, big ones slam. Magnitude kept LOW + fast-
    // decaying so combat reads as punchy without the screen "vibrating" in a dense swarm (user feedback: too shaky).
    if (this.trauma > 0 && shakeEnabled) {
      const s = this.trauma * this.trauma * 0.5
      this.camera.position.x += (Math.random() - 0.5) * s
      this.camera.position.z += (Math.random() - 0.5) * s
      this.camera.position.y += (Math.random() - 0.5) * s * 0.4
    }
    this.trauma = Math.max(0, this.trauma - dt * 3.4) // decays fast so the screen SETTLES the instant the kill stream pauses
    this.camera.lookAt(this.camTarget)
    this.camera.updateMatrixWorld()
  }

  render(): void {
    this.renderer.render(this.scene, this.camera)
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize)
    this.renderer.dispose()
  }
}
